import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, CornerDownLeft } from "lucide-react";

const PHOTON_URL = "https://photon.komoot.io/api";
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Inserisci indirizzo...",
  dataTestid,
  country = "it",
  className = "",
  limit = 6,
  quickChips = null,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const wrapRef = useRef(null);

  const houseNumberMatch = useMemo(() => {
    const m = (value || "").match(/\b(\d+(?:[\/-]?[A-Za-z0-9]+)?)\b/);
    return m ? m[1] : null;
  }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Compatta i risultati in 2 righe: primary (via + civico) e secondary (città + paese)
  const compact = (record) => {
    const parts = record.label.split(", ");
    let primary = parts[0] || "";
    // Se l'utente ha digitato un civico e la via non ce l'ha, lo attacchiamo per mostrarlo all'utente
    const primaryHasNum = /\d/.test(primary);
    if (houseNumberMatch && !primaryHasNum) {
      primary = `${primary} ${houseNumberMatch}`;
    }
    const tailParts = parts.slice(1).filter((p) => !/^\d{4,5}$/.test(p)); // skip CAP
    const secondary = tailParts.join(", ");
    return { primary, secondary, addedNumber: houseNumberMatch && !primaryHasNum };
  };

  const photonToRecord = (f) => {
    const p = f.properties || {};
    const street = p.street ? `${p.street}${p.housenumber ? ` ${p.housenumber}` : ""}` : null;
    const parts = [p.name, street, p.city || p.town || p.village, p.postcode, p.state, p.country].filter(Boolean);
    const label = Array.from(new Set(parts)).join(", ");
    return { label, lat: f.geometry?.coordinates?.[1], lon: f.geometry?.coordinates?.[0], raw: p, _streetKey: (p.street || p.name || "").toLowerCase() + "|" + (p.city || p.town || "").toLowerCase() };
  };

  const nominatimToRecord = (n) => {
    const a = n.address || {};
    const street = a.road ? `${a.road}${a.house_number ? ` ${a.house_number}` : ""}` : null;
    const parts = [n.name && n.name !== a.road ? n.name : null, street, a.city || a.town || a.village, a.postcode, a.state, a.country].filter(Boolean);
    const label = Array.from(new Set(parts)).join(", ") || n.display_name;
    return { label, lat: parseFloat(n.lat), lon: parseFloat(n.lon), raw: a, _streetKey: (a.road || "").toLowerCase() + "|" + (a.city || a.town || "").toLowerCase() };
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    const q = (value || "").trim();
    if (q.length < 3) { setSuggestions([]); setLoading(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const pres = await fetch(`${PHOTON_URL}?q=${encodeURIComponent(q)}&limit=${limit * 2}`, { signal: ctrl.signal });
        const pdata = await pres.json();
        let features = (pdata?.features || [])
          .filter((f) => !country || (f.properties?.countrycode || "").toLowerCase() === country.toLowerCase())
          .map(photonToRecord);

        const photonHasNum = features.some((f) => /\d/.test(f.label.split(",")[0] || ""));
        if (houseNumberMatch && !photonHasNum) {
          try {
            const nres = await fetch(`${NOMINATIM_URL}?q=${encodeURIComponent(q)}&format=json&addressdetails=1&countrycodes=${country}&limit=${limit}`, {
              signal: ctrl.signal,
              headers: { "Accept": "application/json" },
            });
            if (nres.ok) {
              const ndata = await nres.json();
              const nrecords = (ndata || []).map(nominatimToRecord);
              const withNum = nrecords.filter((r) => /\d/.test(r.label.split(",")[0] || ""));
              features = [...withNum, ...features];
            }
          } catch { /* ignore */ }
        }

        const seen = new Set();
        features = features.filter((r) => {
          if (seen.has(r._streetKey)) return false;
          seen.add(r._streetKey);
          return true;
        }).slice(0, limit);

        setSuggestions(features);
        setOpen(true);
      } catch (err) {
        if (err.name !== "AbortError") setSuggestions([]);
      } finally { setLoading(false); }
    }, 280);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, country, limit, houseNumberMatch]);

  // Aggiunge in modo silenzioso il civico digitato se la suggestion non lo include
  const pick = (s) => {
    let finalLabel = s.label;
    const resultHasNum = /\d/.test((s.label.split(",")[0] || ""));
    if (houseNumberMatch && !resultHasNum) {
      const parts = s.label.split(", ");
      if (parts.length > 0) {
        parts[0] = `${parts[0]} ${houseNumberMatch}`;
        finalLabel = parts.join(", ");
      }
    }
    onChange(finalLabel);
    onSelect && onSelect({ ...s, label: finalLabel });
    setOpen(false);
    setSuggestions([]);
  };

  const pickManual = () => {
    setOpen(false);
    setSuggestions([]);
  };

  const trimmedVal = (value || "").trim();
  const showFooter = trimmedVal.length >= 3;

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && highlight < suggestions.length) pick(suggestions[highlight]);
      else if (showFooter) pickManual();
    }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9890] pointer-events-none" />
        <Input
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setHighlight(-1); }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-testid={dataTestid}
          className="pl-8 pr-8"
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#AC5D45]" />}
      </div>

      {open && (suggestions.length > 0 || showFooter) && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-[#E8E2D9] rounded-lg shadow-xl overflow-hidden" data-testid={dataTestid ? `${dataTestid}-suggestions` : undefined}>
          <div className="max-h-72 overflow-auto">
            {suggestions.map((s, i) => {
              const { primary, secondary, addedNumber } = compact(s);
              return (
                <button
                  type="button"
                  key={`${s.lat}-${s.lon}-${i}`}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => pick(s)}
                  className={`w-full text-left px-3 py-2 border-b border-[#F1ECE4] last:border-0 transition ${highlight === i ? "bg-[#FDFBF7]" : "hover:bg-[#FDFBF7]"}`}
                  data-testid={dataTestid ? `${dataTestid}-option-${i}` : undefined}
                >
                  <div className="flex items-center gap-2.5">
                    <MapPin className="w-4 h-4 text-[#AC5D45] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#1E3A5F] truncate">
                        {addedNumber ? (
                          <>
                            {primary.replace(new RegExp(` ${houseNumberMatch}$`), "")} <b className="text-[#AC5D45]">{houseNumberMatch}</b>
                          </>
                        ) : primary}
                      </div>
                      {secondary && <div className="text-[11px] text-[#9C9890] truncate">{secondary}</div>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {showFooter && (
            <button
              type="button"
              onClick={pickManual}
              className="w-full px-3 py-1.5 text-[11px] text-[#635F59] bg-[#FDFBF7] border-t border-[#E8E2D9] hover:bg-[#F1ECE4] transition flex items-center justify-center gap-1.5"
              data-testid={dataTestid ? `${dataTestid}-option-manual` : undefined}
              title="Usa esattamente quello che hai scritto"
            >
              <CornerDownLeft className="w-3 h-3" />
              Premi <kbd className="px-1 bg-white border border-[#E8E2D9] rounded text-[10px]">Invio</kbd> per usare "{trimmedVal.length > 32 ? trimmedVal.slice(0, 32) + "…" : trimmedVal}"
            </button>
          )}
        </div>
      )}

      {quickChips && quickChips.length > 0 && !value && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={dataTestid ? `${dataTestid}-chips` : undefined}>
          {quickChips.map((chip) => (
            <button
              type="button"
              key={chip.label}
              onClick={() => {
                onChange(chip.address);
                onSelect && onSelect({ label: chip.address, lat: chip.lat, lon: chip.lon, raw: {}, _src: "chip" });
              }}
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-[#FDFBF7] border border-[#E8E2D9] text-[#1E3A5F] hover:bg-[#AC5D45] hover:text-white hover:border-[#AC5D45] transition"
              data-testid={dataTestid ? `${dataTestid}-chip-${chip.label.toLowerCase().replace(/\s+/g, "-")}` : undefined}
            >
              <MapPin className="w-3 h-3" /> {chip.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
